import { clearToken, readToken, writeToken } from '@portal/pkg/session/token';

/** Carries the HTTP status, so a call site can tell 401 from 403 from 409. */
export class ApiError extends Error {
	readonly status: number;

	constructor(status: number, message: string) {
		super(message);

		this.name = 'ApiError';
		this.status = status;
	}
}

/**
 *	Whether the server refused outright, which is the only answer that proves
 *	it wrote nothing. A timeout, a dropped connection or a 5xx may each have
 *	applied the change and lost the response on the way back — so a caller
 *	holding something it cannot recreate, such as a freshly generated private
 *	key, must keep it rather than assume the attempt failed.
 */
export function wasRefused(caught: unknown): boolean {
	return caught instanceof ApiError && caught.status < 500;
}

export type ApiMethod = 'GET' | 'POST' | 'PATCH';

export interface ApiRequestOptions {
	/** Sent as a JSON body. Absent means no body and no content type. */
	payload?: unknown;

	/**
	 *	Login is the one call made without a token, and the one whose 401 means
	 *	a wrong password rather than a session that ran out — so it must not
	 *	trip the sign-out below.
	 */
	anonymous?: boolean;
}

/**
 *	Every call the portal makes to devportalapi.
 *
 *	Paths are relative: the SPA and the API share an origin in production, and
 *	the dev server proxies `/api` to stand in for that.
 */
export async function request<T>(
	method: ApiMethod,
	path: string,
	options: ApiRequestOptions = {}
): Promise<T> {
	const { payload, anonymous = false } = options;

	const headers = new Headers({ accept: 'application/json' });

	if (payload !== undefined) {
		headers.set('content-type', 'application/json');
	}

	const token = anonymous ? null : readToken();

	if (token) {
		headers.set('X-TS-Authorization', `Bearer ${token}`);
	}

	const response = await fetch(path, {
		method,
		headers,
		body: payload === undefined ? undefined : JSON.stringify(payload),
	});

	// The session slides: any response may carry a fresh token, and reading it
	// before the status check means an error response renews it too. Offered
	// against the token this request was sent with, so a response that was
	// already in flight when the session ended cannot put one back.
	const renewed = response.headers.get('X-Renewed-Token');

	if (renewed) {
		writeToken(renewed, token);
	}

	if (!response.ok) {
		if (response.status === 401 && !anonymous) {
			clearToken();
		}

		throw new ApiError(response.status, await errorMessage(response));
	}

	return (await response.json()) as T;
}

/**
 *	Error bodies are `{"error": "..."}`. The text is blanked in production for
 *	accounts without the debug capability, so an empty one falls through to the
 *	status line rather than surfacing as a blank message.
 */
async function errorMessage(response: Response): Promise<string> {
	try {
		const body = (await response.json()) as { error?: string };

		return body.error || response.statusText;
	} catch {
		return response.statusText;
	}
}
